// constants
import { KPI_RESULT_TARGET_TYPE_VALUES, KPI_RESULT_TYPE_VALUES, RECURRENCE_ENDS_VALUES } from 'constants/index'
import { KPI_RESULT_TARGET_TYPE_OPTIONS } from 'constants/kpi'
//  utils
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import omit from 'lodash/omit'
import { getValueFromOption } from 'utils/form'
import { generateRanges } from 'utils'

//types
interface Result {
  color: string
  type: string
  greaterValue?: string
  betweenLower?: string
  betweenUpper?: string
  lessValue?: string
}

export const getResultTargetData = (results, resultType) => {
  if (results) {
    return results
      .filter(
        ({ type, betweenLower, betweenUpper, greaterValue, lessValue }) =>
          type && (betweenLower || betweenUpper || greaterValue || lessValue)
      )
      .map(result => {
        let data: Result = { type: getValueFromOption(result.type), color: result.color }

        if (result.type.value === KPI_RESULT_TARGET_TYPE_VALUES.GREATER) {
          data = {
            ...data,
            greaterValue:
              resultType === KPI_RESULT_TYPE_VALUES.SCALE
                ? getValueFromOption(result.greaterValue)
                : result.greaterValue
          }
        }

        if (result.type.value === KPI_RESULT_TARGET_TYPE_VALUES.BETWEEN) {
          data = {
            ...data,
            betweenLower:
              resultType === KPI_RESULT_TYPE_VALUES.SCALE
                ? getValueFromOption(result.betweenLower)
                : result.betweenLower,
            betweenUpper:
              resultType === KPI_RESULT_TYPE_VALUES.SCALE
                ? getValueFromOption(result.betweenUpper)
                : result.betweenUpper
          }
        }

        if (result.type.value === KPI_RESULT_TARGET_TYPE_VALUES.LESS) {
          data = {
            ...data,
            lessValue:
              resultType === KPI_RESULT_TYPE_VALUES.SCALE ? getValueFromOption(result.lessValue) : result.lessValue
          }
        }

        return data
      })
  }
}

export const getResultTargetInitialValue = (result, resultType, resultTypeScale) => {
  if (result) {
    const scaleOption = resultTypeScale.find(({ id }) => id === result)

    return resultType === KPI_RESULT_TYPE_VALUES.SCALE ? { label: scaleOption.label, value: scaleOption.id } : result
  }
}

export const getResultTargetInitialData = (results, resultType, resultTypeScale) => {
  if (results) {
    return results.map(({ greaterValue, betweenLower, betweenUpper, lessValue, type, color }) => {
      const data = { color, type: KPI_RESULT_TARGET_TYPE_OPTIONS.find(i => i.value === type) }

      if (greaterValue) {
        return {
          ...data,
          greaterValue: getResultTargetInitialValue(greaterValue, resultType, resultTypeScale)
        }
      }
      if (betweenLower && betweenUpper) {
        return {
          ...data,
          betweenLower: getResultTargetInitialValue(betweenLower, resultType, resultTypeScale),
          betweenUpper: getResultTargetInitialValue(betweenUpper, resultType, resultTypeScale)
        }
      }
      if (lessValue) {
        return {
          ...data,
          lessValue: getResultTargetInitialValue(lessValue, resultType, resultTypeScale)
        }
      }
    })
  }
}

export const generateResultTarget = (entity, capturedResults, ranges) => {
  const resultTarget = { ...entity.resultTarget, capturedResults }
  const differentResults = get(entity, 'resultTarget.differentResults', [])

  if (isEmpty(differentResults)) {
    return { ...resultTarget, differentResults: ranges.map(range => ({ ...range, results: [] })) }
  }

  const filteredRanges = ranges.filter(({ id, lastDayOfThePeriod }) =>
    differentResults.every(
      result =>
        result.id !== id ||
        (id === result.id && !moment(lastDayOfThePeriod).isSame(moment(result.lastDayOfThePeriod), 'day'))
    )
  )

  return { ...resultTarget, differentResults: [...differentResults, ...filteredRanges] }
}

export const generateCapturedResults = (capturedResults, period, isValueExpected, value) => {
  const captures = capturedResults.filter(
    ({ id, lastDayOfThePeriod }) =>
      id !== period.id ||
      (id === period.id && !moment(lastDayOfThePeriod).isSame(moment(period.lastDayOfThePeriod), 'day'))
  )

  captures.push({
    id: period.id,
    value: isValueExpected ? value : getValueFromOption(value),
    dueDate: period.dueDate,
    lastDayOfThePeriod: period.lastDayOfThePeriod,
    startPoint: period.startPoint
  })

  return captures
}

export const formatPeriod = period => {
  const { lastDayOfThePeriod, nextStartPoint, startPoint } = period

  return {
    ...omit(period, ['dueDate', 'results', 'withStoredData']),
    lastDayOfThePeriod: moment(lastDayOfThePeriod).format('DD MMM Y'),
    nextStartPoint: moment(nextStartPoint).format('DD MMM Y'),
    startPoint: moment(startPoint).format('DD MMM Y')
  }
}

export const getLatestPeriod = (data, recurrence) => {
  const comparePeriods = (a, b) =>
    moment(a.startPoint).unix() >= moment(b.startPoint).unix() &&
    moment(a.lastDayOfThePeriod).unix() >= moment(b.lastDayOfThePeriod).unix()
      ? 1
      : -1

  const index = data.length - 1

  const currentRanges = data.map((fieldName, index) => ({ ...formatPeriod(data.get(index)) }))

  let latest = data.map((field, index) => ({ ...data.get(index) })).sort(comparePeriods)[index]

  if (latest) {
    const { ranges: actual } = generateRanges({ recurrence } as any)

    const duplicateRanges = currentRanges.filter(currentRange =>
      actual.map(formatPeriod).some(range => isEqual(range, currentRange))
    )

    if (latest.withStoredData && duplicateRanges.length !== actual.length) {
      latest = null
    }

    if (!isEmpty(get(latest, 'results', []))) {
      const { ranges } = generateRanges({
        recurrence: { ...recurrence, recurrenceEndsAt: latest.lastDayOfThePeriod }
      } as any)

      const isLatestFromPreviousRecurrence = !ranges
        .map(formatPeriod)
        .some(range => isEqual(range, formatPeriod(latest)))

      const isPreviousRangesMissed = duplicateRanges.length !== actual.length

      if (isLatestFromPreviousRecurrence || isPreviousRangesMissed) {
        latest = null
      }

      if (latest) {
        if (recurrence.recurrenceEnds === RECURRENCE_ENDS_VALUES.ON) {
          const isAfterRecurrenceEnds = moment(latest.startPoint).isAfter(moment(recurrence.recurrenceEndsAt), 'd')

          if (isAfterRecurrenceEnds && duplicateRanges.length < actual.length) {
            latest = null
          }
        }
      }
    }
  }

  return latest
}
